import BaseTypedController from "ips/ac/base/controller/BaseTypedController";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import FilterType from "sap/ui/model/FilterType";
import Sorter from "sap/ui/model/Sorter";
import ODataListBinding from "sap/ui/model/odata/v4/ODataListBinding";
import JSONModel from "sap/ui/model/json/JSONModel";
import Fragment from "sap/ui/core/Fragment";
import formatter from "ips/ac/model/formatter";
import List from "sap/m/List";
import MultiComboBox from "sap/m/MultiComboBox";
import SearchField from "sap/m/SearchField";
import { Button$PressEvent } from "sap/m/Button";
import { QuestionnaireController } from "ips/ac/data/questionnaire/controller/Questionnaire.controller";
import { DropInfo$DropEvent } from "sap/ui/core/dnd/DropInfo";
import CustomListItem from "sap/m/CustomListItem";

/**
 * @namespace ips.ac
 */

type ViewData = {
  controller: QuestionnaireController
}

export default class QuestionPoolController extends BaseTypedController {

  public formatter = formatter;

  private stateModel = new JSONModel({ selectedGuids: [] as string[] });

  private sideList: List;

  private questionnaireController: QuestionnaireController;

  private lastFormGuid: string;

  private ready: Promise<void>;

  private filterDebounceHandle: number;

  onInit() {
    super.onInit();

    // Set directly (not just inherited) so the binding survives being detached from the control tree by removeContentArea()
    this.getView().setModel(this.getModel());
    this.getView().setModel(this.stateModel, "state");

    this.sideList = this.byId("SideList") as List;

    const viewData: ViewData = this.getView().getViewData() as ViewData;
    this.questionnaireController = viewData.controller;

    this.ready = this.bindSideList();
  }

  private async bindSideList() {
    const template = await Fragment.load({
      name: "ips.ac.data.questionnaire.fragment.QuestionPoolItem",
      controller: this,
    }) as CustomListItem;

    this.sideList.bindItems({
      path: "/FormInputs",
      template,
      filters: [
        new Filter("FormDetail/Type_Value", FilterOperator.EQ, "QUESTION_POOL"),
        new Filter("ParentId", FilterOperator.EQ, null),
      ],
      parameters: { $expand: "FormInputPropDetails($orderby=Position),ChildrenDetails($orderby=Position)" },
      sorter: new Sorter("Position"),
      templateShareable: false,
      // No request until filterSideList() resumes it - avoids firing both this initial request and a redundant filtered one
      suspended: true,
    });
  }

  public async updateProperties(formGuid: string, selectedGuids: string[]) {
    // Drives the "visible" binding on the list items - hides already-selected questions without triggering a filter request
    this.stateModel.setProperty("/selectedGuids", selectedGuids);

    if (formGuid !== this.lastFormGuid) {
      // Different questionnaire than last time (or the very first load) - start with a clean search/filter
      this.lastFormGuid = formGuid;
      (this.getView().byId("SideListQuestionFilterBox") as MultiComboBox).setSelectedKeys([]);
      (this.getView().byId("SideListSearchField") as SearchField).setValue("");
    }

    await this.ready;
    this.filterSideList();
  }

  public onFilterSideContent() {
    window.clearTimeout(this.filterDebounceHandle);
    this.filterDebounceHandle = window.setTimeout(() => this.filterSideList(), 300);
  }

  private filterSideList() {
    // Filter for multiComboBox
    const oFilterBox = this.getView().byId("SideListQuestionFilterBox") as MultiComboBox;
    const filterProperty: string = oFilterBox.data("filterProperty");
    const filter = oFilterBox.getSelectedKeys().map(key => new Filter(filterProperty, FilterOperator.EQ, key));

    // Filter for searchField
    const searchField = this.getView().byId("SideListSearchField") as SearchField;
    filter.push(new Filter({
      path: "Description",
      operator: FilterOperator.Contains,
      value1: searchField.getValue(),
      caseSensitive: false,
    }));

    // Combined with the base filters passed to bindItems() in onInit (Control filters are ANDed with the Application filters)
    const binding = this.sideList.getBinding("items") as ODataListBinding;
    binding.filter(new Filter({ filters: filter, and: true }), FilterType.Control);

    if (binding.isSuspended()) {
      // First call after bindSideList() - this single resume() fires the one and only initial request, already filtered
      binding.resume();
    }
  }

  public onSideContentAdd(event: Button$PressEvent) {
    this.questionnaireController.onSideContentAdd(event);
  }

  public onDropDeleteItem(event: DropInfo$DropEvent) {
    const item = event.getParameter("draggedControl") as CustomListItem;
    this.questionnaireController.deleteFormInput(item)
  }
}
